import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { Address } from '../../../../api/app-types/address.app';
import { Client } from '../../../../api/app-types/client.app';
import { ContactPoint, ContactPointCategory } from '../../../../api/app-types/contact-point.app';
import { Gender } from '../../../../api/app-types/gender.app';
import { PersonType } from '../../../../api/app-types/person-type.app';
import {
	CATEGORY_RELATIVE_INITIALS,
	ClarificationValue,
	CreateContactPoint,
	CreateRelative,
} from '../../../../api/app-types/relative.app';
import { CLIENT_API } from '../../../../api/injection-tokens/services.token';
import { AddressCompletion } from '../../../../core/components/address-completion/address-completion';
import { LvrContactPoint } from '../../../../core/components/contact-point/contact-point';
import { LvrDataProtection } from '../../../../core/components/data-protection/data-protection';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrButton } from '../../../../core/components/lvr-button/lvr-button';
import { LvrSearchableSelect } from '../../../../core/components/searchable-select/searchable-select';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { isContactPointComplete } from '../../../../shared/helpers/is-contact-point-complete.func';
import { wrapPromiseWithNotify } from '../../../../shared/helpers/notify.func';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { ClientResolved } from '../../../../shared/routing/resolvers/client.resolver';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';

@Component({
	selector: 'lvr-client-relatives-create',
	templateUrl: './client-relatives-create.html',
	imports: [
		ReactiveFormsModule,
		LvrField,
		MatFormFieldModule,
		LvrInputDirective,
		TranslocoPipe,
		LvrPaddingDirective,
		LvrAutoSaveDirective,
		LvrRestoreActions,
		LvrAppendAsteriskPipe,
		MatSelectModule,
		MatInput,
		MatTooltip,
		MatIcon,
		MatIconButton,
		LvrContactPoint,
		MatCheckbox,
		AddressCompletion,
		LvrDataProtection,
		LvrDefaultActions,
		LvrButton,
		LvrSearchableSelect,
	],
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: { subscriptSizing: 'dynamic' },
		},
	],
})
export class ClientRelativesCreate {
	private readonly fb = inject(FormBuilder);
	private readonly persistentStorage = inject(FormPersistenceService);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly api = inject(CLIENT_API);
	private readonly router = inject(Router);
	private readonly session = inject(SessionManagerService);
	private readonly transloco = inject(TranslocoService);
	private readonly data = toSignal(inject(ActivatedRoute).data);
	private readonly clientResolved = computed<ClientResolved | undefined>(() => {
		return this.data()?.['client'] as ClientResolved | undefined;
	});
	public readonly client = computed<Client>(() => this.clientResolved()?.value as Client);
	public readonly genders = computed(() => this.data()?.['genders'] as Gender[]);
	public readonly contactPointCategories = computed(
		() => (this.data()?.['contactPointCategories'].entities || []) as ContactPointCategory[],
	);
	private readonly personTypeResp = computed(
		() => this.data()?.['categoryPersonTypes'] as PaginatedResponse<PersonType>,
	);
	public readonly personTypes = computed(() => this.personTypeResp().entities || []);
	public readonly personTypeLabel = (personType: PersonType): string => personType.name;
	public readonly environment = inject(ENVIRONMENT);
	private readonly nameRegex = new RegExp(this.environment.nameRegexStr);
	private readonly titleRegex = new RegExp(this.environment.titleRegexStr);

	public readonly communicationWayCount = signal(1);
	private readonly nextCommunicationWayKey = signal(1);
	private readonly communicationWayKeys = signal([0]);
	public readonly communicationWayCountArrayKeys = this.communicationWayKeys.asReadonly();

	public readonly saving = signal(false);

	public readonly draftId = FORM_KEYS.CREATE_CLIENT_RELATIVE;
	public readonly hasError = hasError;
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly canCreateClientEnvironmentsPermission = hasPermission(
		this.session.permissions,
		'ClientEnvironments.Create',
	);
	public readonly form = this.fb.group({
		personCategoryId: this.fb.control<number | undefined>(undefined, {
			validators: [Validators.required],
		}),
		remark: this.fb.control<string>(''),
		title: this.fb.control('', Validators.pattern(this.titleRegex)),
		gender: this.fb.control<number | undefined>(undefined),
		name: this.fb.control('', [Validators.required, Validators.pattern(this.nameRegex)]),
		firstName: this.fb.control('', [Validators.pattern(this.nameRegex)]),
		communicationWays: this.fb.control<ContactPoint[]>([], {
			validators: [Validators.max(5)],
		}),
		isSupervised: this.fb.control<boolean>(false, {}),
		otherText: this.fb.control<string>('', {}),
		address: this.fb.control<Address | undefined>(undefined),
		clarification: this.fb.control<ClarificationValue>({
			hasBeenClarified: false,
			clarifiedAt: undefined,
			typeOfClarification: undefined,
		}),
	});

	private readonly personTypeIdCtrl = this.form.controls.personCategoryId;
	private readonly remarkCtrl = this.form.controls.remark;
	private readonly personTypeIdValue = toSignal(this.personTypeIdCtrl.valueChanges, {
		initialValue: this.personTypeIdCtrl.value,
	});
	public readonly selectedPersonType = computed(() => {
		const selectedId = this.personTypeIdValue();
		return this.personTypes().find((item) => item.id === selectedId);
	});
	public readonly shouldDisplayRemarkField = computed(() => {
		return this.selectedPersonType()?.initials === CATEGORY_RELATIVE_INITIALS.TRUSTED_PERSON_OTHER;
	});
	public constructor() {
		this.handleRemarkField();
	}

	private handleRemarkField() {
		effect(() => {
			const needsRemark = this.shouldDisplayRemarkField();

			if (needsRemark) {
				this.remarkCtrl.addValidators([Validators.maxLength(50)]);
			} else {
				this.remarkCtrl.clearValidators();
				if (this.remarkCtrl.value) this.remarkCtrl.setValue('', { emitEvent: false });
			}
			this.remarkCtrl.updateValueAndValidity({ emitEvent: false });
		});
	}

	public addCommunicationWay() {
		this.communicationWayKeys.update((keys) => [...keys, this.nextCommunicationWayKey()]);
		this.nextCommunicationWayKey.update((key) => key + 1);
		this.communicationWayCount.set(this.communicationWayCount() + 1);
	}

	public removeCommunicationWay(index: number) {
		const keys = this.communicationWayKeys();
		if (index < 0 || index >= keys.length) return;

		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		if (index < values.length) {
			values.splice(index, 1);
			ctrl.setValue(values);
		}
		this.communicationWayKeys.update((currentKeys) => currentKeys.filter((_, keyIndex) => keyIndex !== index));
		this.communicationWayCount.set(this.communicationWayCount() - 1);
	}

	public updateValues(data: { valid: boolean; value: ContactPoint }, index: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;
		const values = (ctrl.value ?? []) as ContactPoint[];
		if (data.valid) {
			if (index >= 0 && index < values.length) {
				values[index] = data.value;
			} else {
				values.push(data.value);
			}
		} else {
			if (index >= 0 && index < values.length) {
				values.splice(index, 1);
			}
		}

		ctrl.setValue([...values]);
	}
	public cancel() {
		this.persistentStorage.deleteDraft('relative', this.draftId);
		this.router.navigate(['/desk/client', this.client().id, 'relatives']);
	}

	public async save() {
		if (this.saving()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const EMPTY_ADDRESS = {
			streetAndHouseNumber: '',
			postalCodeAndCity: '',
		};

		this.saving.set(true);

		try {
			const value = this.form.value;

			const clar = value.clarification ?? {
				hasBeenClarified: false,
				clarifiedAt: undefined,
				typeOfClarification: undefined,
			};

			const body: CreateRelative = {
				name: value.name?.trim() ?? '',
				address: value.address === null ? (EMPTY_ADDRESS as Address) : value.address,
				communicationWays: [],
				firstName: value.firstName?.trim() ?? '',
				isSupervised: value.isSupervised ?? false,
				personCategoryId: value.personCategoryId!,
				remark: value.remark?.trim() ?? '',
				title: value.title?.trim() ?? '',
				genderId: value.gender ?? undefined,
				otherText: value.otherText?.trim() ?? '',
				hasBeenClarified: clar.hasBeenClarified,
				clarifiedAt: clar.clarifiedAt,
				typeOfClarification: clar.typeOfClarification,
			};

			const filteredCws = (value.communicationWays ?? []).filter(isContactPointComplete);

			if (filteredCws.length > 0) {
				const additionalCws: CreateContactPoint[] = filteredCws.map((cp) => ({
					preferredWayOfContact: cp.preferredWayOfContact,
					categoryContactPointId: cp.categoryContactPointId,
					contactValue: cp.contactValue?.trim(),
					remark: cp.remark?.trim(),
				}));

				body.communicationWays!.push(...additionalCws);
			}

			await wrapPromiseWithNotify(
				this.api.createRelative(this.client().id, body),
				this.transloco.translate('base.notification_message.relatives_create_success'),
				this.ntfy,
				this.transloco.translate('base.notification_message.relatives_create_error'),
			);

			this.persistentStorage.deleteDraft('relative', this.draftId);
			await this.router.navigate(['/desk/client', this.client().id, 'relatives']);
		} finally {
			this.saving.set(false);
		}
	}
}
